import { useState } from 'react';
import { Star, X, Loader2, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

export function ReviewModal({ isOpen, onClose, product, orderId, onSuccess }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) {
      toast.error('Please write some feedback');
      return;
    }

    setSubmitting(true);
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${API}/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({
          product_id: product.product_id,
          order_id: orderId,
          rating,
          comment
        })
      });

      if (response.ok) {
        toast.success('Review submitted successfully! Thank you for your feedback.');
        if (typeof onSuccess === 'function') onSuccess();
        if (typeof onClose === 'function') onClose();
      } else {
        const data = await response.json();
        throw new Error(data.message || 'Failed to submit review');
      }
    } catch (error) {
      console.error('Review submission error:', error);
      toast.error(error.message || 'Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
        <button 
          onClick={onClose}
          className="absolute top-6 right-6 p-2 rounded-full hover:bg-surface text-muted-foreground transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <form onSubmit={handleSubmit} className="p-8 md:p-10">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="font-heading text-xl text-secondary leading-tight">Rate & Review</h2>
              <p className="text-sm text-muted-foreground font-body mt-1">Share your experience with us</p>
            </div>
          </div>

          <div className="mb-8">
            <p className="font-body font-bold text-secondary text-sm truncate mb-4">
              {product.name}
            </p>
            
            <div className="flex flex-col items-center justify-center p-6 bg-surface/50 rounded-3xl border border-gray-100">
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-black mb-3">Your Rating</span>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setRating(star)}
                    className="transition-all active:scale-95 p-1"
                  >
                    <Star 
                      className={`w-8 h-8 ${
                        (hoverRating || rating) >= star 
                          ? 'fill-warning text-warning scale-110 shadow-warning/20' 
                          : 'text-gray-200'
                      } transition-all duration-200`}
                    />
                  </button>
                ))}
              </div>
              <span className="mt-4 text-xs font-bold text-warning uppercase">
                {rating === 5 ? 'Excellent!' : rating === 4 ? 'Great' : rating === 3 ? 'Average' : rating === 2 ? 'Poor' : 'Very Bad'}
              </span>
            </div>
          </div>

          <div className="space-y-4 mb-8">
            <label className="text-sm font-bold text-secondary block">Your Review</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What did you like or dislike? How was the quality?"
              className="w-full h-32 rounded-2xl border border-gray-100 bg-surface/30 p-4 font-body text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none resize-none"
              required
            />
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 rounded-2xl h-14 font-body border-gray-100 hover:bg-surface"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="flex-1 rounded-2xl h-14 font-body bg-secondary hover:bg-secondary/90 text-white shadow-lg shadow-secondary/20"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Review'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
